import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "../PageLoader";
import Composer from "./Composer";
import MessageRow from "./MessageRow";
import { wallpaperStyle } from "./DetailsPane";
import {
  Avatar,
  Popover,
  dayLabel,
  lastSeenText,
  brandGradient,
  ChevronLeft,
  ChevronDown,
  CloseIcon,
  SearchIcon,
  DotsIcon,
  StarIcon,
  BellIcon,
  BellOffIcon,
  BanIcon,
  InfoIcon,
  ForwardIcon,
  TrashIcon,
  ChatGlyph,
  CHAT_COL,
} from "./ui";

/** A single row in the thread options popover — icon in a soft tinted chip on
 *  the left, matching the inbox row menu. `tone`: default / brand / danger. */
function MenuItem({ icon: Icon, label, onClick, tone = "default", accent, iconProps }) {
  const chip =
    tone === "danger"
      ? "bg-rose-50 dark:bg-rose-500/15 text-rose-500 group-hover/mi:bg-rose-100 dark:bg-rose-500/15"
      : tone === "brand"
        ? ""
        : "bg-slate-100 text-slate-500 group-hover/mi:bg-slate-200";
  const text = tone === "danger" ? "text-rose-600 dark:text-rose-300" : "text-slate-700";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group/mi flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left text-[13px] font-semibold transition hover:bg-slate-50 ${text}`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${chip}`}
        style={tone === "brand" && accent ? { backgroundColor: `${accent}18`, color: accent } : undefined}
      >
        <Icon className="h-[18px] w-[18px]" {...iconProps} />
      </span>
      {label}
    </button>
  );
}

/** Pane B — the open conversation. */
export default function ThreadPane({
  active,
  messages,
  loadingMsgs,
  loadingMore,
  primary,
  myId,
  otherOnline,
  otherTyping,
  wallpaper,
  unreadAnchorId,
  scrollRef,
  onScroll,
  atBottom,
  unreadCount,
  onJumpBottom,
  onBack,
  onClose,
  embedded,
  detailsOpen,
  onToggleDetails,
  menuOpen,
  onMenuOpen,
  isFavorite,
  isMuted,
  onToggleFavorite,
  onToggleMute,
  onBlock,
  reactFor,
  onMessageMenu,
  onReact,
  onReply,
  onEdit,
  onDelete,
  onCopy,
  onForward,
  onSelectMode,
  onInfo,
  onOpenImage,
  onJumpTo,
  onRetry,
  selectMode,
  selectedIds,
  onToggleSelect,
  onExitSelect,
  onBulkForward,
  onBulkDelete,
  onSelectAll,
  selectedDeletable = 0,
  bulkBusy = null,
  findOpen,
  onFindOpen,
  dragging,
  onDrop,
  onDragOver,
  onDragLeave,
  composer,
}) {
  const [find, setFind] = useState("");
  const [hitIndex, setHitIndex] = useState(0);
  // The options dropdown is portalled (see Popover) so it escapes the page
  // header's sticky/backdrop-blur stacking context, and so it flips/clamps
  // instead of running off a short viewport.
  const optionsBtnRef = useRef(null);

  const hits = useMemo(() => {
    const q = find.trim().toLowerCase();
    if (q.length < 2) return [];
    return messages.filter((m) => !m.deleted && (m.body || "").toLowerCase().includes(q)).map((m) => m.id);
  }, [find, messages]);

  useEffect(() => {
    setHitIndex(0);
  }, [find]);

  useEffect(() => {
    if (!findOpen) setFind("");
  }, [findOpen]);

  const gotoHit = (i) => {
    if (!hits.length) return;
    const next = (i + hits.length) % hits.length;
    setHitIndex(next);
    onJumpTo(hits[next]);
  };

  if (!active) return null;

  /* --------------------------------------------------------------- header */
  const subtitle = otherTyping ? (
    <span className="font-bold" style={{ color: primary }}>
      typing…
    </span>
  ) : otherOnline ? (
    <span className="inline-flex items-center gap-1 font-semibold text-emerald-500">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active now
    </span>
  ) : active.last_seen ? (
    lastSeenText(active.last_seen)
  ) : (
    "Direct message"
  );

  const iconBtn = (label, onClick, children, on) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-slate-100"
      style={on ? { backgroundColor: `${primary}14`, color: primary } : { color: "rgb(var(--c-slate-500))" }}
    >
      {children}
    </button>
  );

  const header = selectMode ? (
    <div className="flex shrink-0 items-center gap-2 border-b border-slate-100 px-3 py-3 sm:px-4" style={{ backgroundColor: `${primary}0a` }}>
      <button type="button" onClick={onExitSelect} aria-label="Cancel selection" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100">
        <CloseIcon className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-extrabold text-slate-900">
          {selectedIds.size ? `${selectedIds.size} selected` : "Select messages"}
        </p>
        {/* The count that matters for the red button: only your own messages
            can be deleted, so say so before the button is pressed rather than
            dropping the rest on the floor. */}
        <p className="truncate text-[11.5px] font-semibold text-slate-400">
          {bulkBusy
            ? `Deleting ${bulkBusy.done} of ${bulkBusy.total}…`
            : selectedIds.size
              ? `${selectedDeletable} of them can be deleted`
              : "Tap any message to select it"}
        </p>
      </div>
      <button type="button" onClick={onSelectAll} className="rounded-full px-3 py-1.5 text-[12px] font-bold transition hover:bg-surface/70" style={{ color: primary }}>
        All
      </button>
      <button type="button" onClick={onBulkForward} disabled={!selectedIds.size || !!bulkBusy} aria-label="Forward selected" title="Forward" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 disabled:opacity-30">
        <ForwardIcon className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={onBulkDelete}
        disabled={!selectedIds.size || !!bulkBusy}
        aria-label="Delete selected"
        title={selectedDeletable ? `Delete ${selectedDeletable}` : "Only your own messages can be deleted"}
        className="flex h-9 items-center gap-1.5 rounded-full px-3 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/15 disabled:opacity-30"
      >
        <TrashIcon className="h-5 w-5" />
        {selectedDeletable > 0 && <span className="text-[12px] font-extrabold">{selectedDeletable}</span>}
      </button>
    </div>
  ) : (
    <div className="relative z-20 flex shrink-0 items-center gap-2.5 border-b border-slate-100 bg-surface px-3 py-2.5 sm:px-4">
      <button type="button" onClick={onBack} aria-label="Back" className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 md:hidden">
        <ChevronLeft className="h-5 w-5" width="2" />
      </button>
      <button type="button" onClick={onToggleDetails} className="flex min-w-0 flex-1 items-center gap-3 rounded-xl py-1 pr-2 text-left transition hover:bg-slate-50">
        <Avatar name={active.name} img={active.img} primary={primary} size={42} ring online={otherOnline} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-extrabold tracking-tight text-slate-900">{active.name}</span>
          <span className="block truncate text-[11px] font-medium text-slate-400">{subtitle}</span>
        </span>
      </button>

      {iconBtn("Search in chat", () => onFindOpen(!findOpen), <SearchIcon className="h-[18px] w-[18px]" />, findOpen)}
      <span className="hidden xl:block">{iconBtn("Chat details", onToggleDetails, <InfoIcon className="h-[18px] w-[18px]" />, detailsOpen)}</span>
      <button
        ref={optionsBtnRef}
        type="button"
        onClick={() => onMenuOpen(!menuOpen)}
        aria-label="Options"
        title="Options"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-slate-100"
        style={{ color: "rgb(var(--c-slate-500))" }}
      >
        <DotsIcon className="h-[18px] w-[18px]" />
      </button>
      {!embedded && (
        <button type="button" onClick={onClose} aria-label="Close" className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 md:flex">
          <CloseIcon className="h-5 w-5" />
        </button>
      )}

      <Popover
        open={menuOpen}
        onClose={() => onMenuOpen(false)}
        anchorRef={optionsBtnRef}
        align="end"
        width={224}
      >
        <div className="flex items-center gap-2 px-2 pb-1.5 pt-1">
          <Avatar name={active.name} img={active.img} primary={primary} size={26} />
          <span className="min-w-0 flex-1 truncate text-[12px] font-extrabold text-slate-800">{active.name}</span>
        </div>
        <div className="mb-1 h-px bg-slate-100" />
        <MenuItem
          icon={StarIcon}
          iconProps={{ filled: isFavorite }}
          tone="brand"
          accent={isFavorite ? "#f59e0b" : primary}
          label={isFavorite ? "Remove from favourites" : "Add to favourites"}
          onClick={() => { onMenuOpen(false); onToggleFavorite(); }}
        />
        <MenuItem
          icon={isMuted ? BellIcon : BellOffIcon}
          label={isMuted ? "Unmute" : "Mute"}
          onClick={() => { onMenuOpen(false); onToggleMute(); }}
        />
        <div className="xl:hidden">
          <MenuItem
            icon={InfoIcon}
            label="Chat details"
            onClick={() => { onMenuOpen(false); onToggleDetails(); }}
          />
        </div>
        <div className="my-1 h-px bg-slate-100" />
        <MenuItem
          icon={BanIcon}
          tone="danger"
          label="Block"
          onClick={() => { onMenuOpen(false); onBlock(); }}
        />
      </Popover>
    </div>
  );

  /* ---------------------------------------------------------- find bar */
  const findBar = findOpen && (
    <div className="chat-fade flex shrink-0 items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2 sm:px-4">
      <SearchIcon className="h-4 w-4 shrink-0 text-slate-400" />
      <input
        autoFocus
        value={find}
        onChange={(e) => setFind(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") gotoHit(hitIndex + (e.shiftKey ? -1 : 1));
          if (e.key === "Escape") onFindOpen(false);
        }}
        placeholder="Find in this conversation…"
        className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-0"
      />
      <span className="shrink-0 text-[11px] font-bold text-slate-400 tabular-nums">
        {hits.length ? `${hitIndex + 1}/${hits.length}` : find.trim().length > 1 ? "0" : ""}
      </span>
      <button type="button" onClick={() => gotoHit(hitIndex - 1)} disabled={!hits.length} aria-label="Previous match" className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-200 disabled:opacity-30">
        <ChevronDown className="h-4 w-4 rotate-180" />
      </button>
      <button type="button" onClick={() => gotoHit(hitIndex + 1)} disabled={!hits.length} aria-label="Next match" className="flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-200 disabled:opacity-30">
        <ChevronDown className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onFindOpen(false)} aria-label="Close search" className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200">
        <CloseIcon className="h-4 w-4" />
      </button>
    </div>
  );

  /* ------------------------------------------------------------ canvas */
  const canvas = (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className="thin-scroll relative min-h-0 flex-1 overflow-y-auto px-3 py-5 sm:px-5"
      style={wallpaperStyle(wallpaper, primary)}
    >
      {/* Shares CHAT_COL with the composer — see the note on it in ui.jsx. */}
      <div className={CHAT_COL}>
        {loadingMsgs ? (
          <div className="space-y-3 pt-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
                <div className={`chat-shimmer h-11 rounded-2xl ${i % 2 ? "w-44" : "w-56"}`} />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2.5 text-center">
            <Avatar name={active.name} img={active.img} primary={primary} size={72} gradientRing />
            <p className="mt-2 text-[15px] font-extrabold text-slate-700">Say hi to {active.name}</p>
            <p className="max-w-[260px] text-[12px] text-slate-400">This is the very beginning of your conversation. Messages are private between the two of you.</p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
              {["👋 Hi!", "Nice to meet you", "Quick question…"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => composer.onQuick(s)}
                  className="rounded-full border border-slate-200 bg-surface px-3 py-1.5 text-[12px] font-semibold text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:shadow"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {loadingMore && (
              <p className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold text-slate-400">
                <Spinner size={11} /> Loading earlier messages…
              </p>
            )}
            {(() => {
              let lastDay = null;
              return messages.map((m, idx) => {
                const dl = dayLabel(m.created_at);
                const showSep = dl && dl !== lastDay;
                lastDay = dl;
                const prev = messages[idx - 1];
                const next = messages[idx + 1];
                const firstOfGroup = !prev || prev.is_me !== m.is_me || dayLabel(prev.created_at) !== dl;
                const lastOfGroup = !next || next.is_me !== m.is_me || dayLabel(next.created_at) !== dl;
                const isHit = hits.length > 0 && hits[hitIndex] === m.id;
                return (
                  <Fragment key={m.id}>
                    {showSep && (
                      <div className="sticky top-0 z-[3] my-4 flex justify-center">
                        <span className="rounded-full bg-surface/80 px-3.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.2)] ring-1 ring-surface/60 backdrop-blur-md">
                          {dl}
                        </span>
                      </div>
                    )}
                    {unreadAnchorId === m.id && (
                      <div className="my-3 flex items-center gap-2 px-1">
                        <span className="h-px flex-1" style={{ backgroundColor: `${primary}40` }} />
                        <span className="rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide" style={{ backgroundColor: `${primary}14`, color: primary }}>
                          Unread messages
                        </span>
                        <span className="h-px flex-1" style={{ backgroundColor: `${primary}40` }} />
                      </div>
                    )}
                    <div className={isHit ? "chat-hit rounded-2xl" : undefined}>
                      <MessageRow
                        m={m}
                        firstOfGroup={firstOfGroup}
                        lastOfGroup={lastOfGroup}
                        primary={primary}
                        myId={myId}
                        otherName={active.name}
                        otherImg={active.img}
                        menuOpen={reactFor === m.id}
                        onMenu={onMessageMenu}
                        selectMode={selectMode}
                        selected={selectedIds.has(m.id)}
                        onToggleSelect={onToggleSelect}
                        onReact={onReact}
                        onReply={onReply}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onCopy={onCopy}
                        onForward={onForward}
                        onSelectMode={onSelectMode}
                        onInfo={onInfo}
                        onOpenImage={onOpenImage}
                        onJumpTo={onJumpTo}
                        onRetry={onRetry}
                      />
                    </div>
                  </Fragment>
                );
              });
            })()}
            {otherTyping && (
              <div className="mt-3 flex items-end gap-2 px-1">
                <Avatar name={active.name} img={active.img} primary={primary} size={28} />
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-surface px-3.5 py-3 shadow-sm ring-1 ring-slate-200/80">
                  {[0, 150, 300].map((d) => (
                    <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div className="h-2" />
          </>
        )}
      </div>

      {/* drag-and-drop overlay */}
      {dragging && (
        <div className="pointer-events-none absolute inset-3 z-10 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-surface/85 backdrop-blur-sm" style={{ borderColor: primary }}>
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
            <ChatGlyph className="h-6 w-6" />
          </span>
          <p className="text-[13px] font-extrabold text-slate-700">Drop the photo to send</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      {header}
      {findBar}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {canvas}
        {/* jump to latest */}
        {!atBottom && !loadingMsgs && messages.length > 0 && (
          <button
            type="button"
            onClick={onJumpBottom}
            aria-label="Jump to latest"
            className="chat-pop absolute bottom-4 right-4 z-[4] flex h-10 items-center gap-1.5 rounded-full border border-slate-200 bg-surface px-3 text-slate-500 shadow-lg transition hover:text-slate-700"
          >
            <ChevronDown className="h-4 w-4" width="2.2" />
            {unreadCount > 0 && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-extrabold text-white" style={brandGradient(primary)}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        )}
      </div>
      {!selectMode && <Composer {...composer} />}
      {selectMode && (
        /* An action bar, not a hint: the actions used to live only in the
           header, which on a tall thread is nowhere near the thumb. */
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 bg-surface px-3 py-3 sm:px-5">
          <div className={CHAT_COL + " flex items-center gap-2"}>
            <span className="hidden min-w-0 flex-1 truncate text-[12px] font-semibold text-slate-400 sm:block">
              {selectedIds.size ? `${selectedIds.size} selected · ${selectedDeletable} deletable` : "Tap any message to select it"}
            </span>
            <button
              type="button"
              onClick={onBulkForward}
              disabled={!selectedIds.size || !!bulkBusy}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-[13px] font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40 sm:flex-none sm:px-5"
            >
              <ForwardIcon className="h-4 w-4" /> Forward{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            <button
              type="button"
              onClick={onBulkDelete}
              disabled={!selectedIds.size || !!bulkBusy}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-500 py-2.5 text-[13px] font-bold text-white shadow-sm transition hover:bg-rose-600 active:scale-[0.98] disabled:opacity-40 sm:flex-none sm:px-5"
            >
              <TrashIcon className="h-4 w-4" />
              {bulkBusy ? `Deleting ${bulkBusy.done}/${bulkBusy.total}…` : `Delete${selectedDeletable ? ` (${selectedDeletable})` : ""}`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
